import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { formatString } from '../../utils';
import { BeatLoader } from 'react-spinners';
import { useMutation } from '@apollo/client';
import { UPDATE_USER } from '../../apollo/mutations/updateUser';
import { createToast } from '../../utils/Toast';
import RadioSelect from '../atoms/Checkbox/HorseProfileCheckBox/AdvanceRadioSelect';

const Toast = createToast();

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

function ManufacturerTable({ manufacturers, loading, sortConfig, handleSort, onEdit, onDelete }) {
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const numericColumns = [];
  const navigate = useNavigate();
  const [updateUser] = useMutation(UPDATE_USER);

  const [updatingId, setUpdatingId] = useState(null);

  const handleTreeMakerUpdate = async (id, value) => {
    setUpdatingId(id);
    try {
      await updateUser({
        variables: {
          input: {
            id: id,
            isTreeMaker: value,
          },
        },
        refetchQueries: ['listManufacturersFull'],
      });
      Toast.fire({
        icon: 'success',
        title: 'Updated successfully',
      });
    } catch (error) {
      console.error('Error updating manufacturer:', error);
      Toast.fire({
        icon: 'error',
        title: 'Failed to update',
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleEdit = (manufacturer) => {
    onEdit(manufacturer);
    setDropdownOpen(null);
  };

  const handleDelete = (manufacturer) => {
    onDelete(manufacturer);
    setDropdownOpen(null);
  };

  const sortedManufacturers = React.useMemo(() => {
    if (sortConfig.key && manufacturers?.length) {
      return [...manufacturers].sort((a, b) => {
        const nestedKeys = ['brand', 'isTreeMaker'];
        let aValue, bValue;

        if (nestedKeys.includes(sortConfig.key)) {
          aValue = a.manufacturerObject?.[sortConfig.key] ?? "";
          bValue = b.manufacturerObject?.[sortConfig.key] ?? "";
        } else {
          aValue = a[sortConfig.key] ?? "";
          bValue = b[sortConfig.key] ?? "";
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;

        return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
      });
    }
    return manufacturers;
  }, [manufacturers, sortConfig]);

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <BeatLoader color={'#2b364b'} />
      </div>
    );
  }

  if (!manufacturers || manufacturers?.length === 0) {
    return (
      <div className="flex justify-center items-center mt-8">
        <p className="admin-dashboard text-3xl text-gray-500">
          No Manufacturers Found. Please check back later.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table
        className="min-w-full overflow-hidden"
        style={{
          border: '1px solid #999',
          borderRadius: '10px',
          borderSpacing: '0',
          tableLayout: 'fixed',
        }}
      >
        <colgroup>
          <col style={{ width: '20%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '20%' }} />
        </colgroup>
        <thead className="thead">
          <tr className="table-head">
            {/* <th
              scope="col"
              align="left"
              className="px-3 py-2"
              style={{
                borderBottom: '1px solid #999',
              }}
            >
              #
            </th> */}
            {[
              { key: 'brand', label: 'Brand Name' },
              { key: 'firstName', label: 'First Name' },
              { key: 'lastName', label: 'Last Name' },
              { key: 'email', label: 'Email' },
              { key: 'isTreeMaker', label: 'Tree Maker' },
            ].map(({ key, label }) =>
              key && (
                <th
                  key={key}
                  scope="col"
                  align={numericColumns.includes(key) ? 'center' : 'left'}
                  className="px-3 py-2 group"
                  style={{
                    borderBottom: '1px solid #999',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSort(key)}
                >
                  {label}
                  <span className="text-sm ml-1">
                    {sortConfig.key === key ? (
                      sortConfig.direction === 'ascending' ? '↑' : '↓'
                    ) : (
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        ↑
                      </span>
                    )}
                  </span>
                </th>
              )
            )}
            {/* <th
              scope="col"
              className="px-3 py-2"
              style={{
                borderBottom: '1px solid #999',
              }}
            >
              Actions
            </th> */}
          </tr>
        </thead>
        <tbody>
          {sortedManufacturers?.map((manufacturer, index) => (
            <tr
              key={index}
              className="cursor-pointer hover:bg-blue-50 transition-colors"
              onClick={() => navigate(`/manufacturers/${manufacturer.id}`)}
            >
              {/* <td align="left" className="table-date px-3 py-4 whitespace-nowrap" style={{ borderBottom: '1px solid #999' }}>
                {index + 1}
              </td> */}
              <td align="left" className="table-order px-3 py-4 whitespace-nowrap" style={{ borderBottom: '1px solid #999' }}>
                {formatString(manufacturer.manufacturerObject?.brand) || 'N/A'}
              </td>
              <td align="left" className="px-3 py-2 whitespace-nowrap table-order !text-[#000] !font-normal" style={{ borderBottom: '1px solid #999' }}>
                {manufacturer.firstName || 'N/A'}
              </td>
              <td align="left" className="px-3 py-2 whitespace-nowrap table-order !text-[#000] !font-normal" style={{ borderBottom: '1px solid #999' }}>
                {manufacturer.lastName || 'N/A'}
              </td>
              <td align="left" className="px-3 py-2 whitespace-nowrap table-order !text-[#000] !font-normal" style={{ borderBottom: '1px solid #999' }}>
                {manufacturer.email || 'N/A'}
              </td>
              <td align="left" className="px-3 py-2 whitespace-nowrap table-order !text-[#000] !font-normal" style={{ borderBottom: '1px solid #999' }}>
                <div onClick={(e) => e.stopPropagation()}>
                  {updatingId === manufacturer.id ? (
                    <div className="w-[100px] h-[35px] flex items-center justify-center">
                      <BeatLoader color={'#2b364b'} size={8} />
                    </div>
                  ) : (
                    <RadioSelect
                      options={[
                        { label: 'Yes', value: true },
                        { label: 'No', value: false },
                      ]}
                      selectedValue={{
                        label: manufacturer.manufacturerObject?.isTreeMaker ? 'Yes' : 'No',
                        value: manufacturer.manufacturerObject?.isTreeMaker
                      }}
                      onChange={(option) => handleTreeMakerUpdate(manufacturer.id, option.value)}
                      menuPortalTarget={document.body}
                      height="35px"
                      className="!w-[100px]"
                      styles={{
                        singleValue: (base) => ({
                          ...base,
                          textAlign: 'left',
                          paddingLeft: '12px',
                          margin: 0,
                        }),
                      }}
                    />
                  )}
                </div>
              </td>
              {/* <td
                align="center"
                className="px-3 py-2 whitespace-nowrap table-order"
                style={{
                  borderBottom: '1px solid #999',
                }}
              >
                <ActionButton
                  icon={ThreeDots}
                  index={index}
                  activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex}
                  actions={[
                    {
                      label: 'Edit',
                      icon: FaEdit,
                      onClick: () => {},
                      className: 'text-gray-700',
                    },
                    {
                      label: 'Delete',
                      icon: FaTrashAlt,
                      onClick: () => {},
                      className: 'text-red-600',
                    },
                  ]}
                />
              </td> */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ManufacturerTable;